'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  ExternalLink,
  FileText,
  Folder as FolderIcon,
  Frame,
  NotebookText,
  Pin,
  PinOff,
  Shapes,
  StickyNote,
  TerminalSquare,
} from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { Tip } from '@/components/ui/tooltip';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  UNNAMED_ITEM_LABEL,
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import { useTranslation } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { getPlacement, type WorkspaceItemDTO } from '@/lib/workspaces/types';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from '@/components/workspace/panels/file-context-menu';

const FALLBACK_ICON: Record<WorkspaceItemDTO['type'], React.ReactNode> = {
  agent: null,
  content: <FileText className="h-4 w-4" />,
  post: <NotebookText className="h-4 w-4" />,
  note: <NotebookText className="h-4 w-4" />,
  shape: <Shapes className="h-4 w-4" />,
  // `terminal` is deprecated post-0020 (split into daemon/ssh/python)
  // but a dock entry can still reference a legacy row until backfill
  // sweeps it, so keep the icon mapping until the union literal is
  // removed.
  terminal: <TerminalSquare className="h-4 w-4" />,
  daemon: <TerminalSquare className="h-4 w-4" />,
  ssh: <TerminalSquare className="h-4 w-4" />,
  python: <TerminalSquare className="h-4 w-4" />,
  files: <FolderIcon className="h-4 w-4" />,
  board: <Frame className="h-4 w-4" />,
  annotation: <StickyNote className="h-4 w-4" />,
  connector: <ArrowRight className="h-4 w-4" />,
};

/**
 * Bottom-centre dock: docked workspace items as a horizontal row of
 * circular tiles, wrapped in a glass-strong pill so the row reads as
 * one cohesive surface (matches the rest of the workspace chrome).
 *
 * Each docked item gets its own tile — items are no longer grouped by
 * `refId`. Pinning the same widget twice was never really a flow
 * because `keptInDock` is per-item, and grouping was masking the count
 * of identical-looking pucks (chat shortcuts to multiple instances of
 * the same agent, in particular) instead of showing each launcher.
 *
 * Click semantics:
 *   - Default (just docked): click restores the widget to the canvas
 *     (clears `docked: true` and re-centres the viewport on it).
 *   - "Kept in dock" (sticky launcher): the widget lives on the canvas
 *     AND keeps a dock tile. Click just pans the viewport — no
 *     un-docking — so the tile behaves like a permanent shortcut.
 *
 * Right-click / long-press opens a context menu with both flavours:
 *   - **Restore**: bring the widget back to the canvas and clear any
 *     "kept" pin.
 *   - **Keep in dock** / **Remove from dock**: toggle the sticky
 *     launcher mode.
 *
 * Reorder: long-press (≥350 ms) a tile to enter drag mode, then move
 * the pointer over neighbouring tiles to nudge them left/right. The
 * dragged tile gets a slight scale + shadow to read as "lifted", and
 * the insertion preview is implicit in the live reorder of the row
 * (no separate ghost element / drop indicator). Release commits the
 * new order via per-item `dockOrder` patches using a midpoint
 * fractional-indexing scheme so adjacent reorders only write the
 * single moved tile.
 *
 * Tooltips use the shared `Tip` primitive so the popover style is
 * consistent with FABs and zoom controls — we explicitly do NOT use
 * native `title=""` attributes anymore (the browser bubble didn't
 * match the rest of the UI and clashed on long display names).
 */
export function DockBar({ className }: { className?: string }) {
  const items = useWorkspaceItems();
  // Tile names resolve live-first through item-label.ts, the rail's rule, so
  // a renamed app is not still "Focus Revent" down here after the rail
  // says Revent (the 2026-09-13 browser pass). Read-only.
  const live = useLiveContentMeta();
  const { patchItemState } = useWorkspaceActions();
  const { t } = useTranslation();

  // A dock tile renders for `docked` (in the dock only) and `pinned`
  // (on canvas + permanent launcher tile) placements. `filed` items
  // belong to the workspace folder tree, NOT the dock, so they're
  // excluded. `getPlacement` maps legacy `docked`/`keptInDock` rows.
  const tiles = useMemo<WorkspaceItemDTO[]>(() => {
    const visible = items.filter((it) => {
      const p = getPlacement(it.state);
      return p === 'docked' || p === 'pinned';
    });
    // Stable sort by `dockOrder`. Items with no `dockOrder` (legacy
    // dock entries, or the first batch after this change rolls out)
    // sort to the END preserving insertion order — i.e. the order
    // they appear in `items[]`. `Array.prototype.sort` in V8 is
    // stable, so equal-key items retain their original relative
    // order.
    return visible.slice().sort((a, b) => {
      const ao = a.state.dockOrder;
      const bo = b.state.dockOrder;
      if (ao == null && bo == null) return 0;
      if (ao == null) return 1;
      if (bo == null) return -1;
      return ao - bo;
    });
  }, [items]);

  // Through the canvas's `ws:focus-item` chokepoint (windows framed at 100%,
  // anything else centred at the current zoom). This used to `setCenter` with a
  // 0.6 zoom floor itself; workspace zoom is disabled. The frame delay stays so
  // a tile restored from the dock is on the canvas before the camera moves.
  const panToItem = useCallback((item: WorkspaceItemDTO) => {
    requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent('ws:focus-item', { detail: { itemId: item.id } }),
      );
    });
  }, []);

  // Default-click behaviour. `pinned` tiles (launcher for a widget
  // already on the canvas) just pan the viewport — the launcher
  // stays put. `docked` tiles restore the widget to the canvas.
  const handleTileClick = (item: WorkspaceItemDTO) => {
    if (getPlacement(item.state) === 'pinned') {
      panToItem(item);
      return;
    }
    patchItemState(item.id, { placement: 'canvas' });
    panToItem(item);
  };

  const restoreToCanvas = (item: WorkspaceItemDTO) => {
    patchItemState(item.id, { placement: 'canvas' });
    panToItem(item);
  };

  // Toggle the permanent launcher. `pinned` → back to plain `canvas`;
  // anything else → `pinned` (visible on canvas + launcher tile).
  const toggleKeepInDock = (item: WorkspaceItemDTO) => {
    if (getPlacement(item.state) === 'pinned') {
      patchItemState(item.id, { placement: 'canvas' });
      return;
    }
    patchItemState(item.id, { placement: 'pinned' });
    panToItem(item);
  };

  const ctx = useFileContextMenu<WorkspaceItemDTO>();

  const buildMenuItems = (target: WorkspaceItemDTO): ContextMenuItem[] => {
    const isKept = getPlacement(target.state) === 'pinned';
    const menu: ContextMenuItem[] = [
      {
        id: 'restore',
        label: 'Restore',
        icon: <ExternalLink className="h-4 w-4" />,
        onSelect: () => restoreToCanvas(target),
      },
    ];
    menu.push({
      id: 'keep',
      label: isKept ? t('workspace.rail.unpinFromDock') : t('workspace.rail.pinToDock'),
      icon: isKept ? (
        <PinOff className="h-4 w-4" />
      ) : (
        <Pin className="h-4 w-4" />
      ),
      onSelect: () => toggleKeepInDock(target),
    });
    return menu;
  };

  // -------------------------------------------------------------------------
  // Drag-to-reorder. Single-pointer state machine that works on both
  // mouse + touch via Pointer Events. We don't pull in @dnd-kit — the
  // dock is small (typically <10 tiles) and the gestures are simple
  // enough that a custom impl avoids the bundle weight and gives us
  // precise control over the long-press threshold + visual feedback.
  // -------------------------------------------------------------------------
  const LONG_PRESS_MS = 350;

  // Live drag state. `draggingId` is null when no drag is active, the
  // workspace item id of the dragged tile otherwise. `hoverIndex` is
  // the index in the SORTED `tiles` array where the dragged tile
  // currently "lives" while the user moves the pointer; we mutate
  // the array reference shown to the user via `previewTiles` so the
  // neighbour shuffle is visible without committing a server patch
  // until release.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  // Refs so the pointer handlers (attached on each tile, but read from
  // the latest live state) always see fresh values without re-binding
  // listeners every render.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;
  const draggingIdRef = useRef<string | null>(null);
  draggingIdRef.current = draggingId;

  // While dragging we render the tile array shuffled to its current
  // hover order so the user sees neighbours slide out of the way.
  const previewTiles = useMemo<WorkspaceItemDTO[]>(() => {
    if (draggingId == null || hoverIndex == null) return tiles;
    const fromIdx = tiles.findIndex((it) => it.id === draggingId);
    if (fromIdx < 0) return tiles;
    const next = tiles.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(hoverIndex, 0, moved);
    return next;
  }, [tiles, draggingId, hoverIndex]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const endDrag = useCallback(
    (commit: boolean) => {
      const id = draggingIdRef.current;
      const idx = hoverIndex;
      setDraggingId(null);
      setHoverIndex(null);
      cancelLongPress();
      if (!commit || id == null || idx == null) return;

      // Final order = the preview array reordered to put `id` at
      // `idx`. We assign `dockOrder` via fractional indexing relative
      // to the new neighbours — only the moved tile writes a new
      // value. Edge cases (drop at start, drop at end, only-one-item)
      // collapse to "midpoint of (prev?.dockOrder ?? 0, next?.dockOrder ?? prev+2)".
      const arr = previewTiles;
      const moved = arr.find((it) => it.id === id);
      if (!moved) return;
      const movedIdx = arr.findIndex((it) => it.id === id);
      const prev = arr[movedIdx - 1];
      const next = arr[movedIdx + 1];
      const prevOrder = prev?.state.dockOrder;
      const nextOrder = next?.state.dockOrder;
      let nextDockOrder: number;
      if (prevOrder != null && nextOrder != null) {
        nextDockOrder = (prevOrder + nextOrder) / 2;
      } else if (prevOrder != null) {
        nextDockOrder = prevOrder + 1;
      } else if (nextOrder != null) {
        nextDockOrder = nextOrder - 1;
      } else {
        // First explicit dockOrder in this dock — anchor near 0 and
        // backfill its neighbours. Backfilling on the FIRST drag is
        // the only time we touch more than one tile; subsequent
        // drags slot into existing gaps.
        nextDockOrder = 0;
        arr.forEach((it, i) => {
          if (it.id === id) return;
          patchItemState(it.id, { dockOrder: i });
        });
      }
      if (moved.state.dockOrder !== nextDockOrder) {
        patchItemState(moved.id, { dockOrder: nextDockOrder });
      }
    },
    [hoverIndex, previewTiles, cancelLongPress, patchItemState],
  );

  const onTilePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, id: string) => {
      // Only the primary pointer drives drag. Right-click / multi-touch
      // fall through to the context menu binding intact.
      if (e.button !== 0 && e.pointerType !== 'touch' && e.pointerType !== 'pen') {
        return;
      }
      cancelLongPress();
      longPressTimer.current = setTimeout(() => {
        // Promote to drag. Pointer is still down (we never released),
        // and `onTilePointerMove` already has its closure on `id`.
        setDraggingId(id);
        const idx = tilesRef.current.findIndex((it) => it.id === id);
        setHoverIndex(idx >= 0 ? idx : null);
        // Suppress the upcoming click. Without this, releasing after
        // a drag would also "restore" the widget. `data-just-dragged`
        // is read by `onClickCapture` below.
        const target = e.currentTarget as HTMLElement;
        target.dataset.justDragged = '1';
      }, LONG_PRESS_MS);
    },
    [cancelLongPress],
  );

  // Index reconciliation: walk the tile DOM and find the slot whose
  // horizontal centre is nearest the pointer. We don't need pixel
  // precision — the dock pill is a flexbox row, and item width is
  // uniform (40px tile + 6px gap).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const computeHoverIndex = useCallback((clientX: number): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const children = Array.from(
      container.querySelectorAll<HTMLElement>('[data-dock-tile]'),
    );
    if (children.length === 0) return null;
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      const centre = rect.left + rect.width / 2;
      const d = Math.abs(clientX - centre);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  }, []);

  const onTilePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (draggingIdRef.current == null) {
        // Long-press hasn't elapsed yet. If the user moved more than
        // a few px, treat it as a normal click attempt (probably
        // scrolling the dock) and cancel the pending drag.
        cancelLongPress();
        return;
      }
      const idx = computeHoverIndex(e.clientX);
      if (idx != null) setHoverIndex(idx);
    },
    [cancelLongPress, computeHoverIndex],
  );

  const onTilePointerUp = useCallback(() => {
    if (draggingIdRef.current != null) {
      endDrag(true);
      return;
    }
    cancelLongPress();
  }, [endDrag, cancelLongPress]);

  const onTilePointerCancel = useCallback(() => {
    if (draggingIdRef.current != null) {
      endDrag(false);
      return;
    }
    cancelLongPress();
  }, [endDrag, cancelLongPress]);

  // Window-level handlers ensure a drag that ends OUTSIDE the dock
  // pill (user dragged the pointer off the tile before releasing)
  // still commits / cleans up. Without this, `draggingId` would
  // remain `true` indefinitely and the next click would be eaten.
  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: PointerEvent) => {
      const idx = computeHoverIndex(e.clientX);
      if (idx != null) setHoverIndex(idx);
    };
    const onUp = () => endDrag(true);
    const onCancel = () => endDrag(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [draggingId, computeHoverIndex, endDrag]);

  if (previewTiles.length === 0) return null;

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'pointer-events-auto liquid-glass rounded-full px-2 py-1.5 select-none shadow-[0_8px_24px_rgba(0,0,0,0.10)]',
          'inline-flex items-center gap-1.5 max-w-[60vw] overflow-x-auto scrollbar-none',
          className,
        )}
        role="toolbar"
        aria-label={t('workspace.canvas.dock.label')}
        // Anchor for the onboarding overlay's `your pinned crew`
        // annotation. Targets the dock pill, not individual tiles, so
        // the arrow still has a stable rect when the user reorders or
        // restores tiles during a tour.
        data-onboarding-anchor="dock"
      >
        {previewTiles.map((item) => {
          const username = item.state.cache?.username as string | undefined;
          const displayName = resolveItemLabel(item, live, UNNAMED_ITEM_LABEL);

          const visual =
            item.type === 'agent' && username ? (
              <Avatar
                seed={username}
                type="agent"
                src={
                  (item.state.cache?.avatarUrl as string | null | undefined) ??
                  null
                }
                className="h-9 w-9 shrink-0 transition-transform group-hover:scale-105"
              />
            ) : (
              <span className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground transition-transform group-hover:scale-105">
                {FALLBACK_ICON[item.type] ?? (
                  <span className="text-[11px] font-mono uppercase">
                    {item.type[0]}
                  </span>
                )}
              </span>
            );

          const isKept = getPlacement(item.state) === 'pinned';
          const isDragging = draggingId === item.id;
          const tooltipLabel = isKept
            ? `${displayName} — focus on canvas`
            : `${displayName} — restore to canvas`;
          const ariaLabel = isKept
            ? `Focus ${displayName}`
            : `Restore ${displayName} to canvas`;

          return (
            <Tip key={item.id} label={tooltipLabel} side="top">
              <div
                data-dock-tile
                onPointerDown={(e) => onTilePointerDown(e, item.id)}
                onPointerMove={onTilePointerMove}
                onPointerUp={onTilePointerUp}
                onPointerCancel={onTilePointerCancel}
                // Suppress click after a drag finishes — see the
                // `dataset.justDragged` flag set in the long-press
                // promotion. Capture phase so we beat the inner
                // button's own click handler.
                onClickCapture={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  if (el.dataset.justDragged === '1') {
                    e.stopPropagation();
                    e.preventDefault();
                    delete el.dataset.justDragged;
                  }
                }}
                className={cn(
                  'relative shrink-0 inline-flex items-center justify-center',
                  // While dragging this tile, lift it visually + drop
                  // it slightly above the row to clearly communicate
                  // grab. Neighbouring tiles shuffle around it.
                  'transition-transform duration-150',
                  isDragging && 'scale-110 -translate-y-1 z-10 cursor-grabbing',
                  draggingId && !isDragging && 'transition-transform',
                )}
                // `touch-action: none` on the wrapper while a drag is
                // active prevents the browser from competing for the
                // gesture (otherwise touch-scroll the parent on iOS
                // hijacks the pointer mid-drag and the drop never
                // commits).
                style={{ touchAction: draggingId ? 'none' : undefined }}
              >
                <button
                  type="button"
                  onClick={() => handleTileClick(item)}
                  // Only wire the right-click context menu — the
                  // touch long-press inside `bindTrigger` would race
                  // our drag-to-reorder long-press (both fire on the
                  // same pointer down) and open the context menu
                  // mid-drag. Mobile users get drag only; the same
                  // actions (Restore, Pin/Unpin) are reachable from
                  // the widget's own header on canvas.
                  onContextMenu={ctx.bindTrigger(item).onContextMenu}
                  className={cn(
                    'group relative shrink-0 inline-flex items-center justify-center',
                    'h-9 w-9 rounded-full outline-none transition-shadow',
                    'focus-visible:ring-2 focus-visible:ring-ring/50',
                    isDragging && 'shadow-[0_8px_18px_rgba(0,0,0,0.18)]',
                  )}
                  aria-label={ariaLabel}
                >
                  {visual}
                  {isKept && (
                    <span
                      className={cn(
                        'absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full',
                        'bg-primary text-primary-foreground',
                        'inline-flex items-center justify-center ring-2 ring-background',
                      )}
                      aria-hidden
                    >
                      <Pin className="h-2 w-2" />
                    </span>
                  )}
                </button>
              </div>
            </Tip>
          );
        })}
      </div>
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? buildMenuItems(ctx.target) : []}
        ariaLabel="Dock tile menu"
      />
    </>
  );
}
